import { SpanStatusCode } from '@opentelemetry/api';
import { OTELApi } from '@grafana/faro-core/';

import { GrafanaFaro } from '../instrument/grafanaFaro';
import { contextManager } from '../instrument/grafanaFaroUtil';
import { PerformanceContext, PerformanceTracker } from './types';
import { waitFor } from './waitFor';
import { getErrorFromUnknown } from '../common/helpers/errors';

const DEFAULT_SPINNER_SELECTOR = '[data-test="loading-spinner-container-indicator"]';
const DEFAULT_LOADING_BLOCK_SELECTOR = '[data-test="loading-block"]';

export const ConsoleTracker: PerformanceTracker = {
  log(...messages: unknown[]): void {
    if (!process.env.DISABLE_LOCAL_FARO_CONSOLE_TRACKER) {
      // eslint-disable-next-line no-console
      console.log('measurement', ...messages);
    }
  },

  pushMeasurement(measureName: string, elapsedTime: number, context: PerformanceContext): void {
    this.log(measureName, elapsedTime, context);
  }
};

export const FaroTracker: PerformanceTracker = {
  log(...messages: unknown[]): void {
    GrafanaFaro.getFaro().api.pushLog(['measurement', ...messages]);
  },

  pushMeasurement(measureName: string, elapsedTime: number, context: PerformanceContext): void {
    this.log('end mark', elapsedTime, context);
    GrafanaFaro.getFaro().api.pushMeasurement(
      {
        type: measureName,
        values: {
          elapsedTime
        }
      },
      { context }
    );
  }
};

type WaitOptions =
  | {
      shouldDetect: true;
      selector?: string;
      disappearanceThreshold?: number;
      shouldWaitForInitialExistence?: boolean;
    }
  | { shouldDetect: false };

type FinishCounting = () => PerformanceContext;

const withoutQuery = (url: string): string => url.split('?')[0];

/** The pivot rows endpoint and its sub-paths (`/calculate`, `/values`), matched on a path-segment boundary. */
const PIVOT_ROWS_PATH = /\/pivot\/rows(\/|$)/;
const PIVOT_COLUMNS_PATH = /\/pivot\/columns(\/|$)/;

/**
 * Counts the pivot row and column requests that reach the network while a measurement runs. Cache
 * hits make no network request, so they are not counted. Returns a function that stops counting and
 * yields the totals as measurement context. Does nothing where `PerformanceObserver` is missing.
 */
function startCountingPivotRequests(): FinishCounting {
  if (typeof PerformanceObserver === 'undefined') return () => ({});
  let rowRequests = 0;
  let columnRequests = 0;
  const count = (entries: PerformanceEntryList) => {
    entries.forEach((entry) => {
      const path = withoutQuery(entry.name);
      if (PIVOT_ROWS_PATH.test(path)) rowRequests += 1;
      if (PIVOT_COLUMNS_PATH.test(path)) columnRequests += 1;
    });
  };
  const observer = new PerformanceObserver((list) => {
    count(list.getEntries());
  });
  try {
    observer.observe({ type: 'resource' });
  } catch {
    return () => ({});
  }
  return () => {
    count(observer.takeRecords());
    observer.disconnect();
    return { rowRequests: String(rowRequests), columnRequests: String(columnRequests) };
  };
}

export class PerformanceMonitor {
  tracker: PerformanceTracker;
  private activeMeasurement: {
    name: string;
    abortController: AbortController;
    annotations: PerformanceContext;
  } | null;

  constructor(tracker: PerformanceTracker) {
    this.tracker = tracker;
    this.activeMeasurement = null;
  }

  async measureUntilDoneLoading({
    measureName,
    spinnerWaitOptions = { shouldDetect: true },
    loadingBlocksWaitOptions = { shouldDetect: true },
    additionalInfo
  }: {
    measureName: string;
    spinnerWaitOptions?: WaitOptions;
    loadingBlocksWaitOptions?: WaitOptions;
    additionalInfo?: { [key: string]: string };
  }): Promise<void> {
    const log = (...messages: string[]): void => {
      this.tracker.log(...messages);
    };

    if (!GrafanaFaro.isInitialized()) {
      return;
    }

    const { context, trace } = GrafanaFaro.getFaro().api.getOTEL() as OTELApi;

    const tracer = trace.getTracer('reporting-app-tracer');
    log('starting measurement', measureName);
    if (!this.activeMeasurement) {
      log('start mark');
      const span = tracer.startSpan(measureName, { root: true, attributes: additionalInfo });
      const spanContext = trace.setSpan(context.active(), span);
      contextManager.setActiveContext(spanContext);

      const start = Date.now();
      const abortController = new AbortController();

      const annotations: PerformanceContext = {};
      this.activeMeasurement = { name: measureName, abortController, annotations };
      const finishCounting = startCountingPivotRequests();

      try {
        log('waiting until no loading blocks');
        let end: null | number = null;
        if (spinnerWaitOptions.shouldDetect) {
          const spinnerSelector = spinnerWaitOptions.selector ?? DEFAULT_SPINNER_SELECTOR;
          end = await waitFor(log, spinnerSelector, {
            // Default for disappearance threshold is 1. Spinner is usually gone after the first check.
            disappearanceThreshold: spinnerWaitOptions.disappearanceThreshold ?? 1,
            // Default for waitForInitialExistence is true. Spinner is usually not there at the start. So we wait for it.
            waitForInitialExistence: spinnerWaitOptions.shouldWaitForInitialExistence ?? true,
            signal: abortController.signal
          });
        }
        if (loadingBlocksWaitOptions.shouldDetect) {
          end = await waitFor(log, loadingBlocksWaitOptions.selector ?? DEFAULT_LOADING_BLOCK_SELECTOR, {
            // Default for disappearance threshold is 5. Loading blocks in the grid tend to stagger since rows are loaded separately and each row has a loading block.
            disappearanceThreshold: loadingBlocksWaitOptions.disappearanceThreshold ?? 5,
            // Default for waitForInitialExistence is false. Loading blocks are usually there before the spinner is gone. So we don't wait for them to appear.
            waitForInitialExistence: loadingBlocksWaitOptions.shouldWaitForInitialExistence ?? false,
            signal: abortController.signal
          });
        }

        if (end) {
          const processingTime = (end - start) / 1000;
          this.tracker.pushMeasurement(measureName, processingTime, {
            canceled: 'false',
            ...additionalInfo,
            ...annotations,
            ...finishCounting(),
            traceId: span.spanContext().traceId
          });
          span.setStatus({ code: SpanStatusCode.OK });
          span.end();
          contextManager.clearActiveContext();
        }
      } catch (unknownError) {
        const error = getErrorFromUnknown(unknownError);
        const processingTime = (Date.now() - start) / 1000;
        this.tracker.pushMeasurement(measureName, processingTime, {
          canceled: 'true',
          error: error.message,
          ...additionalInfo,
          ...annotations,
          ...finishCounting(),
          traceId: span.spanContext().traceId
        });
        span.setStatus({ code: SpanStatusCode.ERROR });
        span.end();
        contextManager.clearActiveContext();
      } finally {
        // Disconnects the request observer on paths that never pushed a measurement; safe to repeat.
        finishCounting();
        this.activeMeasurement = null;
      }
    } else {
      log('skipping since we already measure something');
    }
  }

  /**
   * Adds context to the measurement that is running now, for facts only known after it started
   * (such as which feature flags applied). Does nothing when nothing is being measured.
   */
  annotateActiveMeasurement(info: PerformanceContext): void {
    if (!this.activeMeasurement) return;
    Object.assign(this.activeMeasurement.annotations, info);
  }

  cancelMeasurement({ measureName }: { measureName: string }): void {
    if (!this.activeMeasurement || this.activeMeasurement.name !== measureName) {
      return;
    }

    // Abort the measurement - cleanup will be handled by the catch block
    this.activeMeasurement.abortController.abort();
  }
}

export const monitor =
  process.env.NODE_ENV === 'production' ? new PerformanceMonitor(FaroTracker) : new PerformanceMonitor(ConsoleTracker);
